import type { Plant } from '@/data/plants';
import type { WeatherData, WeatherDay } from '@/lib/weather';
import type { CalculatedPlantingWindow } from '@/lib/seedStartingCalculator';

export type PlantingReadinessStatus = 'ready' | 'wait' | 'caution' | 'unknown';

export interface PlantingReadinessInput {
  plant: Pick<Plant, 'id' | 'name' | 'type' | 'tempRequirements' | 'lifecycle'>;
  zone: number | null | undefined;
  weather?: WeatherData | null;
  today?: string;
  calculatedWindow?: CalculatedPlantingWindow | null;
}

export interface PlantingReadinessResult {
  status: PlantingReadinessStatus;
  reason: string;
  action: string;
  dateGuidance: string;
  weatherGuidance: string;
  riskFactors: string[];
  safeConditions: string[];
}

const WARM_TENDER_IDS = new Set([
  'tomato',
  'pepper',
  'bell-pepper',
  'hot-pepper',
  'eggplant',
  'basil',
  'cucumber',
  'squash',
  'zucchini',
  'watermelon',
  'cantaloupe',
  'melon',
  'okra',
  'sweet-potato',
]);

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function dateToIso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function daysBetween(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T12:00:00Z`).getTime();
  const to = new Date(`${toIso}T12:00:00Z`).getTime();
  return Math.round((to - from) / 86_400_000);
}

function forecastDays(weather: WeatherData): WeatherDay[] {
  const days = [weather.today, ...weather.forecast].filter(Boolean);
  const byDate = new Map<string, WeatherDay>();
  for (const day of days) byDate.set(day.date, day);
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 10);
}

function lowestForecastLow(days: WeatherDay[]): number | null {
  if (days.length === 0) return null;
  return Math.min(...days.map(day => day.minTemp));
}

function firstBelow(days: WeatherDay[], threshold: number): WeatherDay | null {
  return days.find(day => day.minTemp < threshold) ?? null;
}

function firstAtOrBelow(days: WeatherDay[], threshold: number): WeatherDay | null {
  return days.find(day => day.minTemp <= threshold) ?? null;
}

function dateGuidanceFor(window: CalculatedPlantingWindow | null | undefined, today: string): {
  text: string;
  dateStatus: 'before' | 'in-window' | 'after' | 'unknown';
} {
  if (!window) {
    return {
      text: 'Average timing is based on this plant\'s saved planting notes.',
      dateStatus: 'unknown',
    };
  }

  if (window.label) {
    const label = window.label.replace(/^After\s+After\s+/i, 'After ');
    if (window.startDate && daysBetween(today, dateToIso(window.startDate)) > 0) {
      return {
        text: `Average timing: ${label}.`,
        dateStatus: 'before',
      };
    }
    if (window.endDate && daysBetween(dateToIso(window.endDate), today) > 0) {
      return {
        text: `Average timing: ${label}.`,
        dateStatus: 'after',
      };
    }
    return {
      text: `Average timing: ${label}.`,
      dateStatus: 'in-window',
    };
  }

  return {
    text: window.notes[0] ? `Average timing: ${window.notes[0]}` : 'Average timing is not specific enough for a date.',
    dateStatus: window.status === 'needs-zone' ? 'unknown' : 'in-window',
  };
}

function readableDate(date: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${date}T12:00:00Z`));
}

function isWarmTenderCrop(plant: PlantingReadinessInput['plant']): boolean {
  return plant.tempRequirements?.season === 'warm' &&
    !plant.tempRequirements.frostTolerant &&
    (plant.type === 'vegetable' || plant.type === 'herb' || WARM_TENDER_IDS.has(plant.id));
}

function isHardyPerennialOrNativeLike(plant: PlantingReadinessInput['plant']): boolean {
  return plant.tempRequirements?.season === 'perennial' ||
    plant.lifecycle?.kind === 'perennial' ||
    plant.lifecycle?.kind === 'tender-perennial';
}

function statusCopy(status: PlantingReadinessStatus): string {
  if (status === 'ready') return 'Ready';
  if (status === 'wait') return 'Wait';
  if (status === 'caution') return 'Caution';
  return 'Weather data unavailable';
}

export function getPlantingReadiness({
  plant,
  weather,
  today,
  calculatedWindow,
}: PlantingReadinessInput): PlantingReadinessResult {
  const anchorDate = today ?? weather?.today?.date ?? isoToday();
  const date = dateGuidanceFor(calculatedWindow, anchorDate);
  const req = plant.tempRequirements;

  if (!req) {
    return {
      status: 'unknown',
      reason: 'Weather-aware readiness is unavailable because this plant is missing temperature requirements.',
      action: 'Use the average timing as a starting point and check local conditions before planting.',
      dateGuidance: date.text,
      weatherGuidance: 'Live weather readiness is unavailable for this plant.',
      riskFactors: ['Missing plant temperature requirements'],
      safeConditions: ['Use local extension guidance or known crop temperature needs before planting.'],
    };
  }

  if (!weather) {
    return {
      status: 'unknown',
      reason: 'Weather data is not available for your saved location.',
      action: 'Use the average timing, but check your local forecast before planting.',
      dateGuidance: date.text,
      weatherGuidance: 'Live forecast guidance is unavailable.',
      riskFactors: ['No local forecast available'],
      safeConditions: [`Use ${req.minNightTempF}°F as this plant's minimum night-temperature reference.`],
    };
  }

  const days = forecastDays(weather);
  const frost = firstAtOrBelow(days, 32);
  const hardFreeze = firstAtOrBelow(days, 28);
  const minLow = lowestForecastLow(days);
  const coldNight = firstBelow(days, req.minNightTempF);
  const warmTender = isWarmTenderCrop(plant);
  const perennialLike = isHardyPerennialOrNativeLike(plant);
  const warmComfortThreshold = warmTender ? Math.max(50, req.minNightTempF) : req.minNightTempF;
  const warmComfortColdNight = warmTender ? firstBelow(days, warmComfortThreshold) : null;
  const riskFactors: string[] = [];
  const safeConditions = [
    req.frostTolerant
      ? 'No hard freeze in the near forecast for young plants.'
      : 'No frost or freezing temperatures in the near forecast.',
    `Night lows staying near or above ${warmComfortThreshold}°F.`,
  ];

  if (date.dateStatus === 'before') {
    riskFactors.push('Average planting window has not arrived yet.');
  }
  if (!req.frostTolerant && frost) {
    riskFactors.push(`Frost or freezing temperatures are forecast around ${readableDate(frost.date)}.`);
  }
  if (req.frostTolerant && !req.hardFrostTolerant && hardFreeze) {
    riskFactors.push(`A hard freeze is forecast around ${readableDate(hardFreeze.date)}.`);
  }
  if (warmComfortColdNight) {
    riskFactors.push(`Forecast lows dip below ${warmComfortThreshold}°F around ${readableDate(warmComfortColdNight.date)}.`);
  } else if (coldNight && !perennialLike) {
    riskFactors.push(`Forecast lows dip below this crop's ${req.minNightTempF}°F comfort range.`);
  }

  if (date.dateStatus === 'before') {
    return {
      status: 'wait',
      reason: 'The average planting window has not arrived yet.',
      action: 'Keep using protected conditions until the average window and local forecast both look suitable.',
      dateGuidance: date.text,
      weatherGuidance: minLow !== null
        ? `The next 10 days show forecast lows down to ${minLow}°F.`
        : 'The forecast does not include usable nightly lows.',
      riskFactors,
      safeConditions,
    };
  }

  if (!req.frostTolerant && frost) {
    return {
      status: 'wait',
      reason: 'Frost or freezing temperatures are still possible.',
      action: `Do not plant ${plant.name} outside yet. Keep seedlings protected until frost risk clears.`,
      dateGuidance: date.text,
      weatherGuidance: `Your forecast shows ${frost.minTemp}°F around ${readableDate(frost.date)}.`,
      riskFactors,
      safeConditions,
    };
  }

  if (warmTender && warmComfortColdNight) {
    return {
      status: 'wait',
      reason: 'Nights are still too cold for this warm-season crop.',
      action: `Wait to transplant ${plant.name} until nights are consistently above ${warmComfortThreshold}°F.`,
      dateGuidance: date.text,
      weatherGuidance: `Your forecast still has nights below ${warmComfortThreshold}°F.`,
      riskFactors,
      safeConditions,
    };
  }

  if (req.frostTolerant && hardFreeze && !perennialLike) {
    return {
      status: 'caution',
      reason: 'Cool nights are acceptable, but a hard freeze could injure young growth.',
      action: `Plant only if you can protect young ${plant.name} during the freeze.`,
      dateGuidance: date.text,
      weatherGuidance: `A hard freeze near ${hardFreeze.minTemp}°F is forecast around ${readableDate(hardFreeze.date)}.`,
      riskFactors,
      safeConditions,
    };
  }

  if (coldNight && !perennialLike && req.season !== 'cool') {
    return {
      status: 'caution',
      reason: 'The forecast is near this plant\'s lower comfort range.',
      action: `Plant cautiously and protect ${plant.name} if colder nights return.`,
      dateGuidance: date.text,
      weatherGuidance: `Forecast lows dip below ${req.minNightTempF}°F.`,
      riskFactors,
      safeConditions,
    };
  }

  if (date.dateStatus === 'after') {
    return {
      status: 'caution',
      reason: 'The average window has passed, so success depends more on current conditions and remaining season.',
      action: `Plant ${plant.name} only if your season still has enough time and the plant can establish before stress.`,
      dateGuidance: date.text,
      weatherGuidance: minLow !== null
        ? `Current forecast lows stay above ${minLow}°F in the next 10 days.`
        : 'The forecast does not include usable nightly lows.',
      riskFactors,
      safeConditions,
    };
  }

  return {
    status: 'ready',
    reason: 'Average timing and the current forecast both look suitable.',
    action: warmTender
      ? `Plant ${plant.name} once seedlings are hardened off and nights stay warm.`
      : `Plant ${plant.name} when soil and bed conditions are workable.`,
    dateGuidance: date.text,
    weatherGuidance: minLow !== null
      ? `The next 10 days keep forecast lows at or above about ${minLow}°F.`
      : 'No cold-night risk is apparent in the available forecast.',
    riskFactors,
    safeConditions,
  };
}

export function formatPlantingReadinessStatus(status: PlantingReadinessStatus): string {
  return statusCopy(status);
}
